<template>
  <div
    id="gd-server-table-view"
    class="mt-4 gd-table-common gd-server-table relative"
  >
    <div class="flex left-0 z-10 absolute items-center">
      <div v-if="$slots['top-left-content']" class="mr-2">
        <slot name="top-left-content"></slot>
      </div>
      <div v-if="filters">
        <div class="flex items-center mr-2">
          <label class="text-lg mr-2">{{ $t("frontend_filters") }}</label>
          <div class="custom-select-dropdown">
            <select
              class="w-40"
              :value="selectedFilter"
              required
              @change="filterUpdated($event)"
            >
              <option value="" selected>
                {{ $t(filterName) }}
              </option>
              <option
                v-for="type in filters"
                :key="type.id"
                :value="type.value"
              >
                {{ type.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>
    <div class="w-full absolute flex-col items-center new-add-btn-wrapper">
      <div
        class="w-full flex justify-between"
      >
        <div class="w-1/3">
          <p
            v-if="tableTitle"
            class="text-deep-dark text-left text-2xl font-bold"
          >
            {{ $t(tableTitle) }}
          </p>
        </div>
        <div class="flex w-2/3 justify-end">
          <div v-if="$slots['top-right-content']" class="mr-2">
            <slot name="top-right-content"></slot>
          </div>
          <div class="custom-select-dropdown mr-2" v-if="firstFilterData">
            <select
              :value="firstSelectedFilter"
              class="w-40"
              @change="firstFilterUpdated($event)"
            >
              <option value="" selected>
                {{ $t(firstFilterName) }}
              </option>
              <option
                v-for="filterData in this.firstFilterData"
                :key="filterData.id"
                :value="filterData.id"
              >
                {{ filterData.name }}
              </option>
            </select>
          </div>
          <div class="custom-select-dropdown mr-2" v-if="secondFilterData">
            <select
              :value="secondSelectedFilter"
              class="w-40"
              @change="secondFilterUpdated($event)"
            >
              <option value="" selected>
                {{ $t(secondFilterName) }}
              </option>
              <option
                v-for="filterData in this.secondFilterData"
                :key="filterData.id"
                :value="filterData.id"
              >
                {{ filterData.name }}
              </option>
            </select>
          </div>
          <gd-button
            v-if="addNewBtnText"
            primary
            class="w-56 h-10 new-add-btn mr-2"
            @click="addData"
          >
            <span class="mx-2">{{ $t(addNewBtnText) }}</span>
          </gd-button>
          <form v-if="uploadFileBtnText" enctype="multipart/form-data">
            <label class="upload-btn" for="upload" :style="buttonViaImport">
              {{ $t(uploadFileBtnText) }}
            </label>
            <input id="upload" type="file" accept=".csv" @change="onFileChange" />
          </form>
          <gd-button
            v-if="downloadSampleFileBtnText"
            primary
            class="w-56 h-10 new-add-btn ml-2"
            @click="downloadSampleFile"
          >
            <span class="mr-2">{{ $t(downloadSampleFileBtnText) }}</span>
          </gd-button>
          <dropdown-button
            class="ml-2 text-dark bulk-action"
            :style="bulkActionStyle"
            v-if="hasData && modifiedBulkOptions.length"
          >
            <div
              slot="link"
              class="bg-white h-10 flex items-center justify-between inline-block font-medium pl-3 pr-2 py-1 border border-grey-200 rounded bulk-action"
              :style="bulkActionStyle"
            >
              <span class="mr-1 pr-2">{{ $t(bulkActionName) }}</span>
              <i class="fas fa-angle-down" />
            </div>
            <div
              slot="dropdown"
              class="bg-white shadow rounded border border-grey-300 overflow-hidden text-deep-dark text-left"
            >
              <div
                v-for="option in modifiedBulkOptions"
                :key="option.id"
                class="no-underline block p-2 text-grey-darkest bg-white cursor-pointer"
                @click="option.callback"
              >
                {{ $t(option.name) }}
              </div>
            </div>
          </dropdown-button>
        </div>
      </div>
      <div v-if="selectedCount" class="text-left selected-count-text font-bold text-sm">
        {{selectedCount}} {{$t(this.modifiedEntityName)}} {{this.$t("frontend_gd_ui_kit_selected_text")}}
      </div>
    </div>
    <div class="relative">
      <gd-checkbox
        v-if="multiSelection"
        class="mr-2 absolute select-all-checkbox"
        :style="checkBoxStyle"
        v-model="selectCurrentPageEnteries[currentPage - 1]"
        :checkboxClass="checkboxClasses"
        :checkboxTickClass="checkboxTickClasses"
        checkboxIconBgClass="bg-pink border-pink"
        @change="modifyCurrentPageSelection"
      />
      <v-server-table
        class="text-center"
        :style="searchTableSpacing"
        :ref="tableRef"
        :url="url"
        :columns="tableColumnsData"
        :options="tableOptions"
        @loaded="loadedTable()"
        @loading="loadingTable()"
        @filter="filter($event)"
        @pagination="pagination"
        @sorted="sorted"
        @limit="perPageSizeChanged($event)"
      >
        <div v-if="multiSelection" slot="check" slot-scope="props">
          <gd-checkbox
            class="mr-2"
            :checkboxClass="checkboxClasses"
            :checkboxTickClass="checkboxTickClasses"
            checkboxIconBgClass="bg-pink border-pink"
            :value="props.row.checked"
            @change="selectEntry(props.row)"
          />
        </div>
        <div
          v-for="slot in slotData"
          :key="slot.slotName"
          :slot="slot.slotName"
          slot-scope="props"
        >
          <div v-if="slot.slotType == 'computed'">
            <p :class="slot.classes(props)">{{ slot.value(props) }}</p>
          </div>
          <div v-else-if="slot.slotType == 'copy'">
            <p
              v-tooltip="slot.tooltip"
              class="flex items-center text-deep-dark cursor-pointer"
              v-clipboard:copy="slot.value(props)"
              v-clipboard:success="slot.onCopyEmbedCode"
              v-clipboard:error="slot.onErrorEmbedCode"
            >
              {{ slot.value(props) }}
            </p>
          </div>
          <div v-else-if="slot.slotType == 'default' && slot.conditions(props)">
            <p
              :class="slot.classes"
              @click="slot.callback(props)"
              v-html="slot.html(props)"
            ></p>
          </div>
          <div v-else-if="slot.slotType == 'array'">
            <div
              class="w-full flex flex-wrap"
              v-if="props.row[slot.slotName] && props.row[slot.slotName].length"
            >
              <div
                class="mb-2 flex shadow rounded-full text-white bg-blue mr-2 px-4"
                v-for="(category, index) in props.row[slot.slotName]"
                :key="index"
              >
                <p
                  class="p-1-5 px-2 cursor-pointer"
                  @click="slot.callback(category.id)"
                >
                  {{ category.name }}
                </p>
              </div>
            </div>
            <div v-else>
              <p>{{ $t("frontend_groovemail_not_available") }}</p>
            </div>
          </div>
          <div
            v-else-if="slot.slotType == 'icon' && slot.conditions(props)"
            class="flex items-center"
          >
            <div @click="slot.callback(props)" :class="slot.classes">
              <component :is="slot.icon(props)" />
            </div>
            <div class="ml-2" v-if="slot.description.show">
              <p
                class="break-all"
                @click="slot.description.callback(props)"
                v-html="slot.description.html(props)"
              ></p>
            </div>
          </div>
          <div v-else-if="slot.slotType == 'clickable'">
            <p
              v-if="props.row[slot.slotName]"
              class="cursor-pointer font-bold"
              :class="slot.classes"
              @click="slot.callback(props)"
            >
              {{ props.row[slot.slotName] }}
            </p>
            <p v-else>{{ slot.defaultValue }}</p>
          </div>
          <div
            v-else-if="slot.slotType == 'actions' && slot.conditions(props)"
            :class="slot?.classes(props)?.outerDiv"
            class="flex items-center justify-end"
          >
            <div
              v-if="slot.status && slot.status(props) !== ''"
              :title="
                slot.status(props) == 1
                  ? $t('frontend_groovemail_sequence_node_status_active')
                  : $t('frontend_groovemail_sequence_node_status_inactive')
              "
              class="rounded-full w-4 h-4 mr-2"
              :class="{
                'bg-green-extra-dark': slot.status(props) == 1,
                'bg-pink-dark': !slot.status(props),
              }"
            >
              <span></span>
            </div>
            <dropdown-button
              class="float-right pr-4"
              :class="slot?.classes(props)?.dropdown"
            >
              <span
                slot="link"
                class="bg-white h-10 flex items-center inline-block font-medium pl-3 pr-2 py-1 border border-grey-200 rounded"
                :class="slot?.classes(props)?.outerSpan"
              >
                <span class="mr-3 pr-2 font-medium">
                  {{ $t("frontend_actions") }}
                </span>
                <svg
                  class="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                  />
                </svg>
              </span>
              <div
                slot="dropdown"
                class="bg-white shadow rounded border border-grey-300 overflow-hidden cursor-pointer"
                :style="slot.style ? slot.style : ''"
              >
                <div
                  v-for="action in slot.actionList"
                  :key="action.id"
                  @click="action.callback(props)"
                >
                  <div
                    v-if="action.conditions(props.row)"
                    class="no-underline block p-2 text-md font-normal bg-white"
                  >
                    {{ action.name(props) }}
                  </div>
                </div>
              </div>
            </dropdown-button>
          </div>
          <div
            v-else-if="slot.slotType == 'view'"
            class="flex pr-4 items-center"
          >
            <div v-for="view in slot.viewArray" :key="view.id">
              <div
                v-if="view.conditions(props)"
                :title="$t(view?.title)"
                class="cursor-pointer"
                :class="view?.classes"
              >
                <gd-toggle
                  v-if="view.buttonType == 'toggle'"
                  @change="view.callback(props)"
                  class="rounded-full"
                  :class="view?.buttonClass"
                  :value="view.value(props)"
                />
                <gd-button
                  v-else
                  :primary="view?.primary"
                  :onHoverDark="view?.onHoverDark"
                  :disabled="view?.disable && view?.disable(props)"  
                  size="auto"
                  class="icon-btn-width py-4 btn-radius text-base font-semibold"
                  @click="view.callback(props)"
                >
                  <i :class="view?.buttonClass"></i
                ></gd-button>
              </div>
            </div>
          </div>
          <div v-else-if="slot.slotType == 'fraction'">
            <p>
              <span
                class="text-green-dark mx-1 cursor-pointer"
                v-if="slot.value(props).first"
                @click="slot.firstCallback(props)"
              >
                {{ slot.value(props).first }}
              </span>
              <span class="text-yellow-dark mx-1" v-else> 0 </span>
              /
              <span
                class="text-red mx-1 cursor-pointer"
                v-if="slot.value(props).second"
                @click="slot.secondCallback(props)"
                >{{ slot.value(props).second }}</span
              >
              <span class="text-yellow-dark mx-1" v-else> 0 </span>
            </p>
          </div>
          <div v-else-if="slot.slotType == 'tooltip'">
            <div class="cursor-pointer" v-if="slot.conditions(props)">
              <p
                class="overflow-hidden whitespace-nowrap overflow-ellipsis w-80 mr-2"
                v-if="slot.showText"
              >
                {{ slot.textValue(props) }}
              </p>
              <button
                v-tooltip="{
                  content: slot.content(props),
                  placement: 'right-start',
                  classes: ['info'],
                  targetClasses: ['it-has-a-tooltip'],
                  offset: 10,
                  delay: {
                    show: 200,
                    hide: 300,
                  },
                }"
                v-html="slot.html(props)"
              ></button>
            </div>
          </div>
          <div v-else-if="slot.slotType == 'envelopeIcon'">
            <div v-if="slot.conditions(props)">
              <gd-button
                primary
                class=""
                size="icon"
                @click="slot.callback(props)"
              >
                <i class="fa fa-envelope" aria-hidden="true"></i>
              </gd-button>
            </div>
          </div>
          <div v-else-if="slot.slotType == 'status'">
            <p>
              <span
                class="rounded-full py-1 px-6 mr-2"
                :class="slot.classes(props)"
                >{{ slot.statusTitle(props) }}</span
              >
              <span
                v-if="slot.conditions(props)"
                class="text-grey-600 text-xs sm:block md:block lg:block xl:inline-block"
              >
                {{ slot.value(props) }}
              </span>
            </p>
          </div>
          <div
            v-else-if="slot.slotType == 'campaign-view'"
            class="funnelRow h-4 w-4"
          >
            <div
              v-if="slot.firstCondition(props)"
              class="cursor-pointer expandIcon"
              @click="slot.callback(props)"
            >
              <component :is="slot.icon(props)" />
            </div>
            <div v-else-if="slot.secondCondition(props)" class="cursor-pointer">
              <button
                v-tooltip="{
                  content: slot.content(props),
                  placement: 'right-start',
                  classes: ['info'],
                  targetClasses: ['it-has-a-tooltip'],
                  offset: 10,
                  delay: {
                    show: 200,
                    hide: 300,
                  },
                }"
                v-html="slot.html(props)"
              ></button>
            </div>
          </div>
        </div>
      </v-server-table>
    </div>
  </div>
</template>

<script>
import CampaignEmailIcon from "@/assets/images/icons/Camp-Email.svg";
import CampaignSmsIcon from "@/assets/images/icons/Camp-Sms.svg";
import CampaignPostalIcon from "@/assets/images/icons/campaign-postal-icon.svg";
import Snapshot from "@/assets/images/icons/Focus.svg";
import Vue from "vue";

export default {
  props: {
    tableRef: {
      required: false,
    },
    url: {
      required: true,
    },
    tableColumns: {
      required: true,
    },
    tableOptions: {
      required: true,
    },
    tableCondition: {
      required: false,
      default: true,
    },
    tableTitle: {
      required: false,
    },
    addNewBtnText: {
      required: false,
      default: "",
    },
    filters: {
      required: false,
    },
    filterName: {
      required: false,
    },
    selectedFilter: {
      required: false,
    },
    bulkOptions: {
      required: false,
      default() {
        return [];
      },
    },
    bulkActionName: {
      required: false,
      default: "frontend_gd_ui_kit_tables_bulk_actions_text",
    },
    slotData: {
      required: false,
      default() {
        return [];
      },
    },
    uploadFileBtnText: {
      required: false,
    },
    buttonViaImport: {
      required: false,
    },
    downloadSampleFileBtnText: {
      required: false,
    },
    firstFilterName: {
      required: false,
    },
    firstSelectedFilter: {
      required: false,
    },
    firstFilterData: {
      required: false,
    },
    secondFilterName: {
      required: false,
    },
    secondSelectedFilter: {
      required: false,
    },
    secondFilterData: {
      required: false,
    },
    multiSelection: {
      required: false,
    },
    multiSelectUniqueId: {
      required: false,
      default: "id",
    },
    tableStyles: {
      required: false,
      default: () => {
        return {
          checkboxTop: "165px",
          checkboxLeft: "35px",
          searchMarginBottom: "95px",
          bulkActionWidth: "192px",
        };
      },
    },
    entityName: {
      required: false,
      default: () => {
        return {
          entityNameSingular: "frontend_gd_ui_kit_tables_enteries_text_singular",
          entityNamePlural: "frontend_gd_ui_kit_tables_enteries_text_plural",
        };
      },
    },
  },

  data() {
    return {
      isAllSelected: false,
      selectedEnteries: [],
      unselectedEnteries: [],
      selectCurrentPageEnteries: [],
      currentPage: 1,
      totalPages: 0,
      perPage: this.tableOptions.perPage,
      totalCount: 0,
      hasData: false,
      checkboxTickClasses: "h-4 w-4",
      checkboxClasses:
        "flex-none checkBoxSize flex items-center justify-center text-white border-solid border-2 border-black",
    };
  },

  components: {
    CampaignEmailIcon,
    CampaignSmsIcon,
    CampaignPostalIcon,
    Snapshot,
  },

  watch: {
    perPage() {
      this.totalPages = Math.ceil(this.totalCount / this.perPage);
      this.selectCurrentPageEnteries = Array(this.totalPages).fill(false);
    },

    unselectedEnteries() {
      if (this.unselectedEnteries.length === this.$refs[this.tableRef].count) {
        this.selectedEnteries = [];
        this.isAllSelected = false;
        this.selectCurrentPageEnteries = Array(this.totalPages).fill(false);
      }
    },

    selectedEnteries() {
      if (this.selectedEnteries.length === this.$refs[this.tableRef].count) {
        this.unselectedEnteries = [];
        this.isAllSelected = true;
        this.selectCurrentPageEnteries = Array(this.totalPages).fill(true);
      }
    },
  },

  computed: {
    modifiedEntityName() {
      if(this.selectedCount == 1) {
        return this.entityName.entityNameSingular;
      }
      return this.entityName.entityNamePlural;
    },

    selectedCount() {
      let selectedCount = 0;
      if(this.isAllSelected) {
        this.totalCount = this.$refs[this.tableRef].count;   
        selectedCount = this.totalCount - this.unselectedEnteries.length;
      } else {
        selectedCount = this.selectedEnteries.length;
      }
      return selectedCount;
    },

    deselectAllEntityText() {
      return `${this.$t(
        "frontend_gd_ui_kit_tables_deselect_all_enteries_text"
      )} ${this.$t(this.entityName.entityNamePlural)}`;
    },

    selectAllEntityText() {
      return `${this.$t(
        "frontend_gd_ui_kit_tables_select_all_enteries_text"
      )} ${this.$t(this.entityName.entityNamePlural)}`;
    },

    modifiedBulkOptions() {
      const options = [];
      if (this.multiSelection) {
        if (
          this.isAllSelected ||
          (!this.isAllSelected && this.selectedEnteries.length !== 0)
        ) {
          options.push({
            id: 1000,
            name: this.deselectAllEntityText,
            callback: () => this.modifyAllSelection(true),
          });
        }
        if (
          !this.isAllSelected ||
          (this.isAllSelected && this.unselectedEnteries.length !== 0)
        ) {
          options.unshift({
            id: 999,
            name: this.selectAllEntityText,
            callback: () => this.modifyAllSelection(false),
          });
        }
      }

      return [...options, ...this.bulkOptions];
    },

    tableColumnsData() {
      if (this.multiSelection) {
        return ["check", ...this.tableColumns];
      }
      return this.tableColumns;
    },

    checkBoxStyle() {
      return {
        "--import-checkbox-top": this.tableStyles.checkboxTop,
        "--import-checkbox-left": this.tableStyles.checkboxLeft,
      };
    },

    searchTableSpacing() {
      return {
        "--import-search-margin-bottom": this.tableStyles.searchMarginBottom,
      };
    },

    bulkActionStyle() {
      return {
        "--import-bulk-action-width": this.tableStyles.bulkActionWidth,
      };
    },
  },

  mounted() {
    this.$watch(
      () => this.$refs[this.tableRef].count,
      (value) => {
        this.hasData = value > 0;
      }
    );
  },

  methods: {
    addData() {
      this.$emit("addNewData");
    },

    loadedTable() {
      if (this.multiSelection) {
        if (!this.totalCount) {
          this.totalCount = this.$refs[this.tableRef].count;
          this.totalPages = Math.ceil(this.totalCount / this.perPage);
          this.selectCurrentPageEnteries = Array(this.totalPages).fill(false);
        }

        let isSelected = true;
        if (!this.isAllSelected) {
          isSelected = false;
        }
        this.$refs[this.tableRef].data.forEach((element) => {
          if (
            this.unselectedEnteries.includes(element[this.multiSelectUniqueId])
          ) {
            isSelected = false;
          }
        });
        Vue.set(
          this.selectCurrentPageEnteries,
          this.currentPage - 1,
          isSelected
        );
      }
      this.$emit("loaded");
    },

    loadingTable() {
      this.$emit("loading");
    },

    onFileChange(event) {
      this.$emit("onFileChange", event);
    },

    downloadSampleFile() {
      this.$emit("downloadSampleFile");
    },

    updateData(data, count) {
      this.totalPages = Math.ceil(count / this.perPage);
      this.$refs[this.tableRef].data = [];
      if (this.isAllSelected && !this.unselectedEnteries.length) {
        data.forEach((element) => {
          this.$refs[this.tableRef].data.push({ ...element, checked: true });
        });
      } else if (this.isAllSelected && this.unselectedEnteries.length) {
        data.forEach((element) => {
          this.$refs[this.tableRef].data.push({
            ...element,
            checked: !this.unselectedEnteries.includes(
              element[this.multiSelectUniqueId]
            ),
          });
        });
      } else {
        data.forEach((element) => {
          this.$refs[this.tableRef].data.push({
            ...element,
            checked: this.selectedEnteries.includes(
              element[this.multiSelectUniqueId]
            ),
          });
        });
      }
      this.$refs[this.tableRef].count = count;
    },

    // Call reset methods before applying filters.
    resetSelectionData() {
      this.selectedEnteries = [];
      this.unselectedEnteries = [];
      this.isAllSelected = false;
      this.selectCurrentPageEnteries = Array(this.totalPages).fill(false);
      this.$refs[this.tableRef].data.forEach((element, index) => {
        this.$refs[this.tableRef].data[index].checked = false;
      });
      this.getSelectionDataAfterDOMUpdates();
    },

    filterUpdated(event) {
      this.$emit("filterUpdated", event);
    },

    getSelectionDataAfterDOMUpdates() {
      this.$nextTick(() => {
        this.getSelectionData();
      });
    },

    getSelectionData() {
      this.$emit(
        "getSelectionData",
        this.isAllSelected,
        this.selectedEnteries,
        this.unselectedEnteries
      );
    },

    firstFilterUpdated(event) {
      this.$emit("firstFilterUpdated", event);
    },

    secondFilterUpdated(event) {
      this.$emit("secondFilterUpdated", event);
    },

    pagination(event) {
      if (this.multiSelection) {
        this.currentPage = event;
      }
      this.$emit("pagination", event);
    },

    filter(event) {
      this.$emit("filter", event);
    },

    sorted() {
      this.$emit("sorted");
    },

    perPageSizeChanged(event) {
      this.perPage = event;
      this.currentPage = 1;
      this.resetSelectionData();
      this.$emit("perPageSizeChanged", event);
    },

    modifyCurrentPageSelection() {
      this.$refs[this.tableRef].data.forEach((element, index) => {
        this.$refs[this.tableRef].data[index].checked =
          this.selectCurrentPageEnteries[this.currentPage - 1];
        if (this.selectCurrentPageEnteries[this.currentPage - 1]) {
          if (
            !this.selectedEnteries.includes(element[this.multiSelectUniqueId])
          ) {
            this.selectedEnteries.push(element[this.multiSelectUniqueId]);
          }
          this.unselectedEnteries = this.unselectedEnteries.filter(
            (id) => id !== element[this.multiSelectUniqueId]
          );
        } else {
          if (
            !this.unselectedEnteries.includes(element[this.multiSelectUniqueId])
          )
            this.unselectedEnteries.push(element[this.multiSelectUniqueId]);
          this.selectedEnteries = this.selectedEnteries.filter(
            (id) => id !== element[this.multiSelectUniqueId]
          );
        }
      });

      this.setIsAllSelected();
      this.getSelectionDataAfterDOMUpdates();
    },

    setIsAllSelected() {
      let pageNotSelectedCount = 0;
      this.selectCurrentPageEnteries.forEach((element) => {
        if (!element) {
          pageNotSelectedCount++;
        }
      });
      if (
        pageNotSelectedCount === this.selectCurrentPageEnteries.length &&
        !!this.selectedEnteries.length
      ) {
        this.isAllSelected = false;
      } else if (pageNotSelectedCount === 0) {
        this.isAllSelected = true;
      }
    },

    modifyAllSelection(deselectAll) {
      if (deselectAll) {
        this.isAllSelected = false;
        this.selectCurrentPageEnteries = Array(this.totalPages).fill(false);
      } else {
        this.isAllSelected = true;
        this.selectCurrentPageEnteries = Array(this.totalPages).fill(true);
      }
      this.selectedEnteries = [];
      this.unselectedEnteries = [];
      this.$refs[this.tableRef].refresh();
      this.getSelectionDataAfterDOMUpdates();
    },

    selectEntry(rowData) {
      const selectedIndex = this.$refs[this.tableRef].data.findIndex(
        (ele) =>
          ele[this.multiSelectUniqueId] === rowData[this.multiSelectUniqueId]
      );
      if (rowData.checked) {
        if (this.selectCurrentPageEnteries[this.currentPage - 1]) {
          Vue.set(this.selectCurrentPageEnteries, this.currentPage - 1, false);
        }
        this.$refs[this.tableRef].data[selectedIndex].checked = false;
        this.selectedEnteries = this.selectedEnteries.filter((elem) => {
          return elem !== rowData[this.multiSelectUniqueId];
        });
        this.unselectedEnteries.push(rowData[this.multiSelectUniqueId]);
      } else {
        this.$refs[this.tableRef].data[selectedIndex].checked = true;
        this.unselectedEnteries = this.unselectedEnteries.filter((elem) => {
          return elem !== rowData[this.multiSelectUniqueId];
        });
        this.selectedEnteries.push(rowData[this.multiSelectUniqueId]);
        let allDataSelected = true;
        this.$refs[this.tableRef].data.forEach((element) => {
          if (!element.checked) allDataSelected = false;
        });
        if (allDataSelected) {
          Vue.set(this.selectCurrentPageEnteries, this.currentPage - 1, true);
        }
      }
      this.getSelectionDataAfterDOMUpdates();
    },
  },
};
</script>

<style>
#gd-server-table-view #upload {
  opacity: 0;
  position: absolute;
  z-index: -1;
}

#gd-server-table-view .upload-btn {
  margin: 0px !important;
  display: block;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  position: relative;
  width: 230px;
  cursor: pointer;
  border: 0;
  height: 40px;
  border-radius: 6px;
  outline: 0;
  background: #fd6481;
  color: #fff;
  font-size: 16px;
  text-align: center;
  line-height: 40px;
  font-weight: 600 !important;
}

#gd-server-table-view .upload-btn:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  background: #ea3d5e;
}

#gd-server-table-view .upload-btn:after {
  background: #fd6481;
  color: #fff;
  text-align: center;
  position: absolute;
  top: 0;
  left: 0;
  font-weight: 600 !important;
  width: 230px;
  display: block;
  content: var(--import-email-text) !important;
}

#gd-server-table-view .checkBoxSize {
  height: 18px;
  width: 18px;
}
</style>


<style lang="scss">
#gd-server-table-view {
  .selected-count-text {
    color: #FD6481;
  }
  
  .bulk-action {
    width: var(--import-bulk-action-width);
  }

  .select-all-checkbox {
    top: var(--import-checkbox-top);
    left: var(--import-checkbox-left);
    z-index: 10;
  }

  .new-add-btn {
    font-weight: 600;
    border-radius: 6px;
    font-size: 16px;
  }

  .new-add-btn-wrapper {
    z-index: 9;
    right: 0;
    top: 70px;
  }

  .VueTables__heading {
    color: #191d3a;
  }

  .VueTables__child-row-toggler--closed::before {
    content: url("data:image/svg+xml; utf8, <svg width='20' height='20' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path d='m3.707031 7l-.707031.707031 6.125 6.125 1.875 1.875 1.875-1.875 6.125-6.125-.707031-.707031-6.125 6.125-1.167969 1.167969-1.167969-1.167969-6.125-6.125' /></svg>");
    padding-right: 3px;
    vertical-align: middle;
  }

  .VueTables__child-row {
    text-align: center;
    border-bottom: 1px solid #ddd;
    border-left: 5px solid #fd6481;
  }

  .VueTables__child-row-toggler--open::before {
    content: url("data:image/svg+xml; utf8, <svg width='20' height='20' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path d='m3.707031 1044.3622l-.707031-.707 6.125-6.125 1.875001-1.875 1.875 1.875 6.125 6.125-.707032.707-6.125-6.125-1.167968-1.168-1.167969 1.168-6.125 6.125' transform='translate(0-1030.3622)'/></svg>");
    padding-right: 3px;
    vertical-align: middle;
  }

  tr:nth-child(even) {
    background: #f8f8fc;
  }

  .vue-pagination-ad {
    text-align: center;
  }

  .VuePagination__count {
    display: none;
  }

  .expandIcon {
    display: none;
  }

  .funnelRow:hover .expandIcon {
    display: block !important;
  }

  tr.VueTables__child-row > td {
    padding: 0px !important;
  }

  .VueTables__row:last-child {
    border-bottom: none;
  }

  .VueTables--server .table-responsive {
    background: #efeff5;
  }

  .VueTables__sort-icon {
    float: none;
    color: #191d3a;
    margin-left: 8px;
  }

  .VueTables__table {
    overflow: auto !important;
    border: none;
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    padding: 0px;
    background: white;
    border-radius: 6px;
    box-shadow: 20px 20px 40px rgba(22, 29, 37, 0.05);
  }

  .VueTables__table td {
    text-align: left;
    padding: 16px;
    font-size: 14px;
    color: #2d3540;
    font-weight: 600;
    .pull-right {
      float: none;
    }
  }

  .VueTables__table th:last-child span {
    display: none;
  }

  .VueTables__table th {
    text-align: left;
    padding: 25px 12px;
    font-size: 14px;
    border-bottom: 2px solid #dcdcea;
    .pull-right {
      float: none;
    }
  }

  #VueTables_th--check {
    padding: 25px 50px;
  }

  th:nth-child(1),
  td:nth-child(1) {
    padding-left: 35px;
  }

  .VueTables__search {
    float: right;
    .VueTables__search-field {
      margin-bottom: var(--import-search-margin-bottom);
      position: relative;
      label {
        display: none;
      }
    }

    .VueTables__search-field .VueTables__search__input {
      height: 40px;
      width: 250px;
      padding: 6px 12px 6px 35px;
      font-size: 14px;
      color: #555;
      background-color: #fff;
      background-image: none;
      border: 1px solid #dcdcea;
      border-radius: 4px;
      margin: 10px 0px 0px 10px;
    }

    .VueTables__search-field:before {
      width: 24px;
      height: 24px;
      content: "";
      display: inline-block;
      position: absolute;
      left: 17px;
      top: 18px;
      background-image: url("~@/assets/images/icons/search_query.png");
    }
  }

  label {
    font-weight: bold;
  }

  .VueTables__limit {
    display: block;
  }

  .VuePagination__pagination li {
    float: left;
    font-size: 14px;
    text-align: center;
    color: #191d3a;
    padding: 4px;
    font-weight: 600;
    margin-left: 4px;
    line-height: 1.25;
  }

  .VueTables__limit {
    margin-top: 10px;
    position: absolute;
    bottom: -60px;
    left: 0;
    .VueTables__limit-field {
      label {
        display: none;
      }
      &:before {
        content: "Show";
        display: inline-block;
        color: #69788c;
        font-size: 12px;
        margin-right: 10px;
        font-weight: 600;
      }
      &:after {
        content: "";
        background: url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Ctitle%3Edown-arrow%3C%2Ftitle%3E%3Cg%20fill%3D%22%23000000%22%3E%3Cpath%20d%3D%22M10.293%2C3.293%2C6%2C7.586%2C1.707%2C3.293A1%2C1%2C0%2C0%2C0%2C.293%2C4.707l5%2C5a1%2C1%2C0%2C0%2C0%2C1.414%2C0l5-5a1%2C1%2C0%2C1%2C0-1.414-1.414Z%22%20fill%3D%22%23000000%22%3E%3C%2Fpath%3E%3C%2Fg%3E%3C%2Fsvg%3E")
          no-repeat;
        width: 20px;
        font-size: 12px;
        position: absolute;
        top: 12px;
        right: 5px;
        color: #545452;
        height: 25px;
        pointer-events: none;
        cursor: pointer;
      }
      select {
        border: 1px solid #dcdcea;
        border-radius: 6px;
        height: 40px;
        width: 80px;
        background: #fff;
        padding: 0 28px 0 8px;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        cursor: pointer;
      }
    }
  }

  .VueTables--server .VueTables__limit {
    margin-bottom: 20px;
    bottom: -30px;
    .VueTables__limit-field {
      display: flex;
      align-items: center;
    }
  }

  .VuePagination__pagination-item.page-item.VuePagination__pagination-item-prev-page,
  .VuePagination__pagination-item.page-item.VuePagination__pagination-item-next-page {
    cursor: pointer;
  }

  .VuePagination.row.col-md-12.justify-content-center {
    margin-top: 30px;
  }

  .VuePagination__pagination-item {
    border: none;
    background-color: transparent;
    color: #191d3a;
  }

  .VuePagination__pagination-item .active,
  .VuePagination__pagination .paginationActive,
  .page-item.active {
    border-radius: 6px;
    background-color: #fd6481;
    color: #ffffff;
  }

  .VuePagination__pagination li a,
  .VuePagination__pagination li a.active {
    padding: 3px 4px;
  }

  .VuePagination__pagination-item.page-item.VuePagination__pagination-item-prev-chunk,
  .VuePagination__pagination-item.paginationPrev.VuePagination__pagination-item-prev-chunk,
  .VuePagination__pagination-item.page-item.VuePagination__pagination-item-next-chunk,
  .VuePagination__pagination-item.paginationNext.VuePagination__pagination-item-next-chunk {
    display: none;
  }

  .VuePagination {
    .VuePagination__pagination {
      display: flex !important;
    }
  }

  .VuePagination__pagination-item.paginationPrev,
  .VuePagination__pagination-item.paginationNext {
    background: transparent;
  }

  .VuePagination {
    .paginationPrev,
    .paginationNext {
      cursor: pointer;
    }
  }

  .VuePagination {
    .paginationPrev.paginationDisabled,
    .paginationNext.paginationDisabled {
      color: #8f92ab;
      cursor: not-allowed;
    }
  }
}
</style>
